import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { calculateLength, formatTime } from './utils';
import './App.css'; 

const API_URL = process.env.REACT_APP_API_URL;

// --- 輔助函數 ---
const getPreview = (post) => {
    if (post.title && post.title.trim()) {
        return <span className="history-title">{post.title}</span>;
    }
    const contentStr = post.content || '';
    const firstLine = contentStr.split('\n')[0] || ''; 
    const previewText = firstLine.length > 20 ? firstLine.substring(0, 20) + '...' : firstLine;
    return <span className="history-preview">{previewText}</span>;
};

const parseContent = (content) => {
    if (!content) return null;
    const lines = content.split('\n');

    return lines.map((line, index) => {
        const trimmedLine = line.trim();

        const isImage = /^https?:\/\/.*\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff)(\?.*)?$/i.test(trimmedLine);
        if (isImage) {
            return (
                <div key={index} className="media-container">
                    <img src={trimmedLine} alt="Blog media" className="media-content-img" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    <a href={trimmedLine} target="_blank" rel="noopener noreferrer" className="media-card" style={{ display: 'none' }}>
                        <div className="media-icon" style={{ backgroundColor: '#95a5a6' }}>🖼️</div>
                        <div className="media-info"><span className="media-title">Image Link (Preview Failed)</span><span className="media-link">{trimmedLine}</span></div>
                    </a>
                </div>
            );
        }

        const isHeic = /^https?:\/\/.*\.(heic|heif)(\?.*)?$/i.test(trimmedLine);
        if (isHeic) {
            return (
                <div key={index} className="media-container">
                    <a href={trimmedLine} target="_blank" rel="noopener noreferrer" className="media-card">
                        <div className="media-icon" style={{ backgroundColor: '#3498db' }}>🍏</div>
                        <div className="media-info"><span className="media-title">Apple HEIC Image</span><span className="media-link">{trimmedLine}</span></div>
                    </a>
                </div>
            );
        }

        const youtubeMatch = trimmedLine.match(/^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
        if (youtubeMatch) {
            const videoId = youtubeMatch[4];
            const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
            return (
                <div key={index} className="media-container">
                    <a href={videoUrl} target="_blank" rel="noopener noreferrer" style={{ position: 'relative', display: 'block' }}>
                        <img src={thumbnailUrl} alt="Video Thumbnail" className="media-content-video" />
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '0', height: '0', borderTop: '15px solid transparent', borderBottom: '15px solid transparent', borderLeft: '25px solid #fff', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}></div>
                    </a>
                </div>
            );
        }

        const platforms =[
            { name: 'Bilibili', regex: /bilibili\.com\/video\/([a-zA-Z0-9]+)/, color: '#fb7299', icon: '📺' },
            { name: 'Vimeo', regex: /vimeo\.com\/(\d+)/, color: '#1ab7ea', icon: '🎬' },
            { name: 'TikTok', regex: /tiktok\.com/, color: '#000000', icon: '🎵' },
            { name: 'Instagram', regex: /instagram\.com\/(p|reel)/, color: '#e1306c', icon: '📸' },
            { name: 'Twitter / X', regex: /(twitter\.com|x\.com)/, color: '#1da1f2', icon: '🐦' }
        ];

        for (const platform of platforms) {
            if (platform.regex.test(trimmedLine)) {
                return (
                    <div key={index} className="media-container">
                        <a href={trimmedLine} target="_blank" rel="noopener noreferrer" className="media-card">
                            <div className="media-icon" style={{ backgroundColor: platform.color }}>{platform.icon}</div>
                            <div className="media-info"><span className="media-title">Watch on {platform.name}</span><span className="media-link">{trimmedLine}</span></div>
                        </a>
                    </div>
                );
            }
        }

        return <p key={index} style={{ margin: '0.8em 0', minHeight: trimmedLine ? 'auto' : '1em' }}>{line}</p>;
    });
};

const SuccessView = ({ postData, onReset }) => {
    const shareUrl = `${window.location.origin}/${postData.shortId}`;
    const [copyStatus, setCopyStatus] = useState('Copy Link');
    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl).then(() => { setCopyStatus('Copied!'); setTimeout(() => setCopyStatus('Copy Link'), 2000); });
    };
    return (
        <div className="container success-view" style={{ textAlign: 'center', padding: '50px 0' }}>
            <div className="success-icon" style={{ fontSize: '50px', color: 'var(--warning-color)', marginBottom: '20px' }}>✓</div>
            <h2>Published Successfully!</h2>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={handleCopy}>{copyStatus}</button>
                <Link to={`/${postData.shortId}`}><button className="btn btn-secondary">View Post</button></Link>
            </div>
            <div style={{ marginTop: '40px' }}>
                <button onClick={onReset} style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', textDecoration: 'underline' }}>Return to Editor</button>
            </div>
        </div>
    );
};

// ==========================================
// Component: Home (首頁：雙欄佈局)
// ==========================================
const Home = () => {
    // --- 狀態管理 ---
    const [posts, setPosts] = useState([]); 
    const [folders, setFolders] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState('all'); // 'all', 'uncategorized', or folder.shortId
    
    const [isAdmin, setIsAdmin] = useState(false); 
    const [showLogin, setShowLogin] = useState(false); 
    const[passwordInput, setPasswordInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    // 寫作區狀態
    const [title, setTitle] = useState(''); 
    const [content, setContent] = useState('');
    const [postFolderId, setPostFolderId] = useState(''); // 新文章所屬資料夾
    const[publishedData, setPublishedData] = useState(null);
    const textareaRef = useRef(null);

    // 資料夾管理狀態
    const [newFolderName, setNewFolderName] = useState('');
    const [editingFolderId, setEditingFolderId] = useState(null);
    const [editFolderName, setEditFolderName] = useState('');

    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [processingFolderId, setProcessingFolderId] = useState(null);

    // 初始化與資料獲取
    useEffect(() => {
        fetchData();
        const savedKey = localStorage.getItem('blog_admin_key');
        if (savedKey) verifyKey(savedKey, true); 
    }, []);

    useEffect(() => { fetchData(); }, [isAdmin]); 

    const fetchData = async () => {
        const key = localStorage.getItem('blog_admin_key');
        try {
            const config = key ? { headers: { 'x-admin-key': key } } : {};
            // 同時獲取文章與資料夾
            const [postsRes, foldersRes] = await Promise.all([
                axios.get(`${API_URL}/api/posts`, config),
                axios.get(`${API_URL}/api/folders`, config)
            ]);
            if (Array.isArray(postsRes.data)) setPosts(postsRes.data);
            if (Array.isArray(foldersRes.data)) setFolders(foldersRes.data);
        } catch (err) { console.error("Failed to fetch data", err); }
    };

    // --- 驗證邏輯 ---
    const verifyKey = async (key, silent = false) => {
        try {
            await axios.post(`${API_URL}/api/verify`, {}, { headers: { 'x-admin-key': key } });
            setIsAdmin(true); setShowLogin(false); localStorage.setItem('blog_admin_key', key);
            if (!silent) setPasswordInput(''); 
        } catch (err) {
            if (!silent) alert('Incorrect Admin Key');
            localStorage.removeItem('blog_admin_key'); setIsAdmin(false);
        }
    };
    const handleLoginSubmit = () => verifyKey(passwordInput);
    const handleKeyDown = (e) => { if (e.key === 'Enter') handleLoginSubmit(); };

    // --- 編輯器邏輯 ---
    const handleInput = (e) => {
        setContent(e.target.value);
        const target = e.target;
        const currentScrollY = window.scrollY; 
        target.style.height = 'auto';
        target.style.height = target.scrollHeight + 'px';
        window.scrollTo(0, currentScrollY); 
    };

    const handlePaste = () => {
        setTimeout(() => {
            if (textareaRef.current) {
                const rect = textareaRef.current.getBoundingClientRect();
                if (rect.bottom > window.innerHeight) {
                    window.scrollBy({ top: rect.bottom - window.innerHeight + 60, behavior: 'smooth' });
                }
            }
        }, 10);
    };

    const handleSubmit = async () => {
        if (!content.trim()) return;
        const adminKey = localStorage.getItem('blog_admin_key');
        try {
            const res = await axios.post(`${API_URL}/api/posts`, 
                { title, content, folderId: postFolderId || null }, 
                { headers: { 'x-admin-key': adminKey } }
            );
            setPublishedData(res.data); setContent(''); setTitle(''); setPostFolderId(''); fetchData();
        } catch (err) { alert('Failed to publish'); }
    };

    const handleDelete = async (shortId) => {
        if (!window.confirm('Delete this post?')) return;
        const adminKey = localStorage.getItem('blog_admin_key');
        try {
            await axios.delete(`${API_URL}/api/posts/${shortId}`, { headers: { 'x-admin-key': adminKey } });
            fetchData(); // 重新抓取以更新資料夾統計
        } catch (err) { alert('Failed to delete'); }
    };

    const handleToggleVisibility = async (shortId) => {
        const adminKey = localStorage.getItem('blog_admin_key');
        try {
            await axios.put(`${API_URL}/api/posts/${shortId}/toggle-hidden`, {}, { headers: { 'x-admin-key': adminKey } });
            fetchData(); // 重新抓取以更新資料夾統計
        } catch (err) { alert('Failed to toggle visibility'); }
    };


    // 資料夾管理邏輯 (加入防連點機制) 
    const handleCreateFolder = async () => {
        if (!newFolderName.trim() || isCreatingFolder) return;
        setIsCreatingFolder(true); // 按鈕變灰
        try {
            await axios.post(`${API_URL}/api/folders`, { name: newFolderName }, { headers: { 'x-admin-key': localStorage.getItem('blog_admin_key') } });
            setNewFolderName(''); fetchData();
        } catch (err) { alert('Failed to create folder'); }
        finally { setIsCreatingFolder(false); } // 恢復按鈕
    };

    const handleRenameFolder = async (shortId) => {
        if (!editFolderName.trim()) return setEditingFolderId(null);
        setProcessingFolderId(shortId);
        try {
            await axios.put(`${API_URL}/api/folders/${shortId}`, { name: editFolderName }, { headers: { 'x-admin-key': localStorage.getItem('blog_admin_key') } });
            setEditingFolderId(null); fetchData();
        } catch (err) { alert('Failed to rename folder'); }
        finally { setProcessingFolderId(null); }
    };

    const handleToggleFolder = async (shortId) => {
        setProcessingFolderId(shortId);
        try {
            await axios.put(`${API_URL}/api/folders/${shortId}/toggle-hidden`, {}, { headers: { 'x-admin-key': localStorage.getItem('blog_admin_key') } });
            fetchData();
        } catch (err) { alert('Failed to toggle folder'); }
        finally { setProcessingFolderId(null); }
    };

    const handleDeleteFolder = async (shortId) => {
        if (!window.confirm('Delete folder? Posts inside will be moved to Uncategorized.')) return;
        setProcessingFolderId(shortId);
        try {
            await axios.delete(`${API_URL}/api/folders/${shortId}`, { headers: { 'x-admin-key': localStorage.getItem('blog_admin_key') } });
            if (selectedFolderId === shortId) setSelectedFolderId('all');
            fetchData();
        } catch (err) { alert('Failed to delete folder'); }
        finally { setProcessingFolderId(null); }
    };

    // 移動文章的函數
    const handleMovePost = async (shortId, newFolderId) => {
        const adminKey = localStorage.getItem('blog_admin_key');
        try {
            await axios.put(`${API_URL}/api/posts/${shortId}/move`, 
                { folderId: newFolderId }, 
                { headers: { 'x-admin-key': adminKey } }
            );
            fetchData(); // 重新抓取資料以更新畫面
        } catch (err) { alert('Failed to move post'); }
    };

    // 過濾當前顯示的文章
    const displayedPosts = posts.filter(post => {
        if (selectedFolderId === 'all') return true;
        if (selectedFolderId === 'uncategorized') return !post.folderId;
        return post.folderId === selectedFolderId;
    });

    if (publishedData) return <SuccessView postData={publishedData} onReset={() => setPublishedData(null)} />;

    return (
        <div className="blog-layout">
            
            {/* 左側：資料夾側邊欄 */}
            <aside className="sidebar">
                <h3 style={{ marginTop: 0, color: 'var(--text-main)' }}>Folders</h3>
                <ul className="folder-list">
                    {/* All Posts */}
                    <li className={`folder-item ${selectedFolderId === 'all' ? 'active' : ''}`} onClick={() => setSelectedFolderId('all')}>
                        <div className="folder-item-header">
                            <span>All Posts</span>
                            <span style={{ fontSize: '0.8em', opacity: 0.8 }}>({posts.length})</span>
                        </div>
                    </li>
                    
                    {/* 動態資料夾列表 */}
                    {folders.map(f => (
                        <li key={f.shortId} className={`folder-item ${selectedFolderId === f.shortId ? 'active' : ''}`} onClick={() => setSelectedFolderId(f.shortId)}>
                            
                            {/* 編輯模式 */}
                            {editingFolderId === f.shortId ? (
                                <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
                                    <input type="text" className="folder-input" value={editFolderName} onChange={e => setEditFolderName(e.target.value)} autoFocus />
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <button className="btn-action btn-primary" onClick={() => handleRenameFolder(f.shortId)}>Save</button>
                                        <button className="btn-action" onClick={() => setEditingFolderId(null)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                /* 正常顯示模式 */
                                <>
                                    <div className="folder-item-header">
                                        <span>
                                            {f.name} 
                                            {f.isHidden && isAdmin && <span className="badge-hidden">Hidden</span>}
                                        </span>
                                        <span style={{ fontSize: '0.8em', opacity: 0.8 }}>
                                            {isAdmin ? `(${f.publicCount}/${f.totalCount})` : `(${f.publicCount})`}
                                        </span>
                                    </div>
                                    
                                    {/* 管理員操作區 */}
                                    {isAdmin && (
                                        <div className="folder-actions" onClick={e => e.stopPropagation()}>
                                            <button disabled={processingFolderId === f.shortId} onClick={() => handleToggleFolder(f.shortId)}>{f.isHidden ? 'Unhide' : 'Hide'}</button>
                                            <button disabled={processingFolderId === f.shortId} onClick={() => { setEditingFolderId(f.shortId); setEditFolderName(f.name); }}>Rename</button>
                                            <button disabled={processingFolderId === f.shortId} onClick={() => handleDeleteFolder(f.shortId)} style={{ color: processingFolderId === f.shortId ? 'gray' : 'var(--danger-color)' }}>Delete</button>
                                        </div>
                                    )}
                                </>
                            )}
                        </li>
                    ))}

                    {/* Uncategorized */}
                    <li className={`folder-item ${selectedFolderId === 'uncategorized' ? 'active' : ''}`} onClick={() => setSelectedFolderId('uncategorized')}>
                        <div className="folder-item-header">
                            <span>Uncategorized</span>
                            <span style={{ fontSize: '0.8em', opacity: 0.8 }}>({posts.filter(p => !p.folderId).length})</span>
                        </div>
                    </li>
                </ul>

                {/* 新建資料夾 (僅管理員) */}
                {isAdmin && (
                    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                        <input 
                            type="text" className="folder-input" placeholder="New folder name..." 
                            value={newFolderName} onChange={e => setNewFolderName(e.target.value)} 
                            disabled={isCreatingFolder}
                        />
                        <button className="btn btn-secondary" style={{ width: '100%', margin: 0 }} onClick={handleCreateFolder} disabled={isCreatingFolder}>
                            {isCreatingFolder ? 'Adding...' : '+ Add Folder'}
                        </button>
                    </div>
                )}
            </aside>

            {/* 右側：主內容區 */}
            <main className="main-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                    <h2 style={{ margin: 0, fontFamily: '"Noto Serif TC", serif', color: 'var(--text-main)' }}>Sunny Blog</h2>
                    {!isAdmin && !showLogin && (
                        <button onClick={() => setShowLogin(true)} style={{ background: 'none', border: '1px solid var(--border-color)', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Write</button>
                    )}
                    {isAdmin && (
                        <button onClick={() => { setIsAdmin(false); localStorage.removeItem('blog_admin_key'); }} style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', fontSize: '0.8rem' }}>Logout</button>
                    )}
                </div>

                {/* 登入框 */}
                {showLogin && !isAdmin && (
                    <div style={{ marginBottom: '30px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <input type={showPassword ? "text" : "password"} placeholder="Enter Admin Key" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} onKeyDown={handleKeyDown} style={{ padding: '8px', paddingRight: '50px', borderRadius: '4px', border: '1px solid var(--border-color)', width: '200px', background: 'var(--card-bg)', color: 'var(--text-main)' }} />
                            <button onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold', padding: '0 5px' }}>{showPassword ? "Hide" : "Show"}</button>
                        </div>
                        <button className="btn btn-primary" style={{ padding: '8px 15px' }} onClick={handleLoginSubmit}>Verify</button>
                        <button className="btn btn-secondary" style={{ padding: '8px 15px' }} onClick={() => setShowLogin(false)}>Cancel</button>
                    </div>
                )}

                {/* 編輯器 */}
                {isAdmin && (
                    <div style={{ marginBottom: '60px', animation: 'fadeIn 0.5s' }}>
                        {/* 選擇資料夾下拉選單 (給新文章用的) */}
                        <select className="folder-select" value={postFolderId} onChange={(e) => setPostFolderId(e.target.value)}>
                            <option value="">📁 Select Folder (Uncategorized)</option>
                            {folders.map(f => <option key={f.shortId} value={f.shortId}>{f.name}</option>)}
                        </select>

                        <input type="text" className="input-title" placeholder="Title (Optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
                        <textarea
                            ref={textareaRef} className="textarea-editor" style={{ borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                            value={content} onChange={handleInput} onPaste={handlePaste} placeholder="What's on your mind today?" rows={1}
                        />
                        <div className="toolbar">
                            <span>Characters: {calculateLength(content)}</span>
                            <button className="btn btn-primary" onClick={handleSubmit} disabled={content.trim().length === 0}>Publish</button>
                        </div>
                        <hr />
                    </div>
                )}

                {/* 文章列表 */}
                <div>
                    <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '10px', color: 'var(--text-light)', fontSize: '1rem' }}>
                        {selectedFolderId === 'all' ? 'All Posts' : selectedFolderId === 'uncategorized' ? 'Uncategorized' : folders.find(f => f.shortId === selectedFolderId)?.name || 'Posts'}
                    </h3>
                    {displayedPosts.length === 0 ? (
                        <p style={{ color: 'var(--text-light)', fontStyle: 'italic' }}>No posts in this folder.</p>
                    ) : (
                        <ul className="history-list" style={{ listStyle: 'none', padding: 0 }}>
                            {displayedPosts.map(post => (
                                <li key={post.shortId} className={`history-item ${post.isHidden ? 'post-hidden' : ''}`}>
                                    <div style={{ flex: 1 }}>
                                        <Link to={`/${post.shortId}`} style={{ textDecoration: 'none' }}>{getPreview(post)}</Link>
                                        {post.isHidden && <span className="badge-hidden">Hidden</span>}
                                        <span style={{ display: 'block', fontSize: '12px', marginTop: '4px', color: 'var(--text-light)' }}>
                                            {formatTime(post.createdAt)}
                                            {/* 顯示所屬資料夾標籤 */}
                                            {post.folderId && <span style={{ marginLeft: '10px', padding: '2px 6px', background: 'var(--bg-color)', borderRadius: '4px', border: '1px solid var(--border-color)' }}>📁 {folders.find(f => f.shortId === post.folderId)?.name || 'Unknown'}</span>}
                                        </span>
                                    </div>
                                    
                                    {/* 移動資料夾下拉選單 */}
                                    {isAdmin && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <select 
                                                className="folder-select" 
                                                style={{ margin: 0, padding: '2px 6px', fontSize: '0.8rem', width: 'auto' }}
                                                value={post.folderId || ''}
                                                onChange={(e) => handleMovePost(post.shortId, e.target.value)}
                                            >
                                                <option value="">Uncategorized</option>
                                                {folders.map(f => <option key={f.shortId} value={f.shortId}>{f.name}</option>)}
                                            </select>

                                            <button className="btn-action btn-warning" onClick={() => handleToggleVisibility(post.shortId)} title={post.isHidden ? "Make Public" : "Hide from Public"}>{post.isHidden ? "Unhide" : "Hide"}</button>
                                            <button className="btn-action btn-danger" onClick={() => handleDelete(post.shortId)} title="Delete Permanently">Delete</button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>
        </div>
    );
};

// ==========================================
// Component: PostView (單頁面：加入修改資料夾功能)
// ==========================================
const PostView = () => {
    const { shortId } = useParams();
    const [post, setPost] = useState(null);
    const [folders, setFolders] = useState([]); // 存儲資料夾列表供編輯時選擇
    const [isEditing, setIsEditing] = useState(false);
    const[editContent, setEditContent] = useState('');
    const [editTitle, setEditTitle] = useState('');
    const[editFolderId, setEditFolderId] = useState(''); // 編輯時的資料夾
    const editTextareaRef = useRef(null);
    
    const adminKey = localStorage.getItem('blog_admin_key');
    const isAdmin = !!adminKey; 

    useEffect(() => {
        const config = adminKey ? { headers: { 'x-admin-key': adminKey } } : {};
        
        // 同時獲取文章詳情和資料夾列表
        Promise.all([
            axios.get(`${API_URL}/api/posts/${shortId}`, config),
            axios.get(`${API_URL}/api/folders`, config).catch(() => ({ data: [] })) // 讀者可能拿不到完整資料夾，忽略錯誤
        ]).then(([postRes, foldersRes]) => {
            setPost(postRes.data); 
            setEditContent(postRes.data.content); 
            setEditTitle(postRes.data.title || '');
            setEditFolderId(postRes.data.folderId || '');
            if (Array.isArray(foldersRes.data)) setFolders(foldersRes.data);

            const pageTitle = postRes.data.title || postRes.data.content.substring(0, 20);
            document.title = `${pageTitle} - Sunny Blog`; 
        }).catch(err => { console.error(err); alert('Post not found (or access denied)'); });
    }, [shortId, adminKey]); 

    useEffect(() => {
        if (isEditing && editTextareaRef.current) {
            editTextareaRef.current.style.height = 'auto';
            editTextareaRef.current.style.height = editTextareaRef.current.scrollHeight + 'px';
        }
    }, [isEditing]);

    const handleEditInput = (e) => {
        setEditContent(e.target.value);
        const target = e.target;
        const currentScrollY = window.scrollY;
        target.style.height = 'auto';
        target.style.height = target.scrollHeight + 'px';
        window.scrollTo(0, currentScrollY);
    };

    const handleEditPaste = () => {
        setTimeout(() => {
            if (editTextareaRef.current) {
                const rect = editTextareaRef.current.getBoundingClientRect();
                if (rect.bottom > window.innerHeight) {
                    window.scrollBy({ top: rect.bottom - window.innerHeight + 60, behavior: 'smooth' });
                }
            }
        }, 10);
    };

    const handleCancelEdit = () => {
        if (window.confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
            setIsEditing(false); setEditContent(post.content); setEditTitle(post.title || ''); setEditFolderId(post.folderId || '');
        }
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API_URL}/api/posts/${shortId}`, 
                { title: editTitle, content: editContent, folderId: editFolderId || null }, 
                { headers: { 'x-admin-key': adminKey } }
            );
            setPost({ ...post, title: editTitle, content: editContent, folderId: editFolderId || null, updatedAt: new Date().toISOString() });
            setIsEditing(false);
        } catch (err) {
            if (err.response && err.response.status === 403) alert('Permission Denied');
            else alert('Failed to update');
        }
    };

    if (!post) return <div className="container" style={{textAlign:'center', color: 'var(--text-main)'}}>Loading...</div>;

    return (
        <div className="container">
            <div style={{ marginBottom: '20px' }}>
                <Link to="/" style={{ textDecoration: 'none', color: 'var(--text-muted)', fontSize: '0.9rem' }}>← Back to Home</Link>
            </div>

            {isEditing ? (
                <>
                    {/* 編輯模式：選擇資料夾 */}
                    <select className="folder-select" value={editFolderId} onChange={(e) => setEditFolderId(e.target.value)}>
                        <option value="">📁 Select Folder (Uncategorized)</option>
                        {folders.map(f => <option key={f.shortId} value={f.shortId}>{f.name}</option>)}
                    </select>

                    <input type="text" className="input-title" placeholder="Title (Optional)" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', marginBottom: '0' }} />
                    <textarea
                        ref={editTextareaRef} className="textarea-editor" style={{ minHeight: '300px', borderTop: 'none' }}
                        value={editContent} onChange={handleEditInput} onPaste={handleEditPaste}
                    />
                    <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
                        <button className="btn btn-secondary" onClick={handleCancelEdit}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleUpdate}>Save</button>
                    </div>
                </>
            ) : (
                <>
                    {post.title && <h1 style={{ fontFamily: '"Noto Serif TC", serif', marginBottom: '10px' }}>{post.title} {post.isHidden && <span className="badge-hidden" style={{ marginLeft: '15px', fontSize: '0.5em', verticalAlign: 'middle' }}>Hidden</span>}</h1>}
                    {!post.title && post.isHidden && <div style={{ marginBottom: '20px' }}><span className="badge-hidden">Hidden Post</span></div>}
                    
                    <div style={{ fontSize: '18px', textAlign: 'justify', lineHeight: '1.8', color: 'var(--text-main)' }}>
                        {parseContent(post.content)}
                    </div>
                    
                    <hr />
                    
                    <div style={{ fontSize: '14px', color: 'var(--text-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            Published: {formatTime(post.createdAt)}
                            {post.createdAt !== post.updatedAt && <span style={{ marginLeft: '15px' }}>(Edited: {formatTime(post.updatedAt)})</span>}
                            {/* 顯示所屬資料夾 */}
                            {post.folderId && <span style={{ marginLeft: '15px', padding: '2px 6px', background: 'var(--bg-color)', borderRadius: '4px', border: '1px solid var(--border-color)' }}>📁 {folders.find(f => f.shortId === post.folderId)?.name || 'Unknown'}</span>}
                        </div>
                        {isAdmin && <button onClick={() => setIsEditing(true)} style={{ background: 'none', border: 'none', color: 'var(--link-color)', cursor: 'pointer', fontWeight: 'bold' }}>Edit</button>}
                    </div>
                </>
            )}
        </div>
    );
};

function App() {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/:shortId" element={<PostView />} />
            </Routes>
        </Router>
    );
}

export default App;